<template>
  <div class="quote-widget md:sticky md:top-20 md:max-h-[calc(100dvh-6rem)] overflow-x-clip md:overflow-y-auto md:overscroll-contain space-y-5 rounded-xl border border-slate-200/80 bg-white/60 p-3.5 md:p-4 shadow-sm backdrop-blur-sm dark:border-slate-700/60 dark:bg-slate-800/40 dark:shadow-gray-700">
    <ClientOnly>
      <div class="quote-widget__calendar">
        <VueDatePicker
          v-model="dateRange"
          :range="{ noDisabledRange: true }"
          :min-date="minDate ?? undefined"
          :max-date="maxDate ?? undefined"
          :disabled-dates="isDateDisabled"
          :highlight="highlightedDates"
          :time-config="{ enableTimePicker: false }"
          :formats="{ input: formatDate }"
          auto-apply
          inline
          :dark="isDark"
          @range-start="onRangeStart"
        >
          <template #day="{ day, date }">
            <span :title="isBookedDate(date) ? $t('quote.bookedTooltip') : undefined">{{ day }}</span>
          </template>
        </VueDatePicker>
      </div>
    </ClientOnly>

    <div class="grid grid-cols-2 gap-3">
      <div>
        <div class="font-semibold text-sm">{{ $t('quote.checkin') }}</div>
        <div class="mt-1 text-sm font-medium tabular-nums">
          {{ checkin ? formatDMY(checkin) : '—' }}
        </div>
      </div>
      <div>
        <div class="font-semibold text-sm">{{ $t('quote.checkout') }}</div>
        <div class="mt-1 text-sm font-medium tabular-nums">
          {{ checkout ? formatDMY(checkout) : '—' }}
        </div>
      </div>
    </div>

    <div class="-mt-4">
      <label class="font-semibold text-sm">{{ $t('quote.guests') }}</label>
      <div class="relative mt-1">
        <Icon name="lucide:users" class="size-4 absolute top-3 start-4 text-slate-400" />
        <select
          v-model.number="guests"
          class="w-full ps-10 py-2 px-3 h-10 bg-transparent dark:bg-slate-900 rounded border border-gray-200 dark:border-gray-800 text-[16px]"
        >
          <option v-for="n in capacity" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
    </div>

    <div v-if="quotePending" class="text-sm text-slate-500">{{ $t('quote.loading') }}</div>

    <div
      v-else-if="quoteError"
      class="text-sm text-red-500"
    >
      {{ $t('quote.errorGeneric') }}
    </div>

    <div
      v-else-if="quote && !quote.available"
      class="space-y-2"
    >
      <p class="text-sm text-amber-600 dark:text-amber-400">{{ unavailableMessage }}</p>
      <NuxtLink
        v-if="checkin && checkout"
        :to="localePath({ path: '/search', query: { from: checkin, to: checkout, guests } })"
        class="inline-flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600"
        @click="onFallbackClick"
      >
        <Icon name="lucide:search" class="size-4" />
        {{ $t('searchAllCta.findAnother') }}
        <Icon name="lucide:arrow-right" class="size-3.5" />
      </NuxtLink>
    </div>

    <div v-else-if="quote && quote.available" class="space-y-2 text-sm">
      <!-- Collapsible breakdown — closed by default, mirrors Airbnb's
           "Show price breakdown" pattern. Native <details> means no JS,
           full keyboard support, and SSR-correct initial state. -->
      <details class="group quote-widget__breakdown">
        <summary
          class="flex items-center justify-between gap-2 cursor-pointer list-none select-none text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <span class="inline-flex items-center gap-1.5">
            {{ $t('quote.breakdownToggle') }}
            <Icon
              name="lucide:chevron-down"
              class="size-3.5 transition-transform duration-200 group-open:rotate-180"
              aria-hidden="true"
            />
          </span>
          <span
            v-if="cleaningsIncluded > 0"
            class="inline-flex items-center gap-1 normal-case tracking-normal text-emerald-700 dark:text-emerald-400"
          >
            <Icon name="lucide:sparkles" class="size-3.5" aria-hidden="true" />
            {{ $t('prices.calculator.row.cleaningsIncluded', cleaningsIncluded, { named: { n: cleaningsIncluded } }) }}
          </span>
        </summary>

        <div class="mt-3 space-y-2">
          <!-- Per-period breakdown — one row per pricing tier in the stay -->
          <div
            v-for="(grp, i) in breakdownGroups"
            :key="`grp-${i}`"
            class="flex justify-between"
          >
            <span class="text-slate-600 dark:text-slate-300">
              {{ $t('prices.calculator.row.subtotal', { nights: grp.nights, rate: `$${grp.rate}` }) }}
            </span>
            <span class="tabular-nums">${{ grp.subtotal }}</span>
          </div>

          <!-- Cleaning fee — line item with free-cleanings note inline -->
          <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span class="text-slate-600 dark:text-slate-300">{{ $t('prices.calculator.row.cleaning') }}</span>
            <span v-if="quote.cleaningUsd > 0" class="tabular-nums">${{ quote.cleaningUsd }}</span>
            <span v-else class="text-emerald-600 dark:text-emerald-400">
              {{ $t('prices.calculator.row.cleaningIncluded') }}
            </span>
          </div>

          <div v-if="quote.extraGuestUsd > 0" class="flex justify-between">
            <span class="text-slate-600 dark:text-slate-300">
              {{ $t('prices.calculator.row.extraGuest', { nights: quote.nights, rate: `$${extraGuestRate}` }) }}
            </span>
            <span class="tabular-nums">${{ quote.extraGuestUsd }}</span>
          </div>
        </div>
      </details>

      <hr class="border-slate-200 dark:border-slate-700 my-1">

      <div class="flex justify-between font-semibold text-base">
        <span>{{ $t('prices.calculator.row.total') }}</span>
        <span class="tabular-nums">${{ quote.totalUsd }}</span>
      </div>

      <div class="flex justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{{ $t('prices.calculator.row.prepayment') }}</span>
        <span class="tabular-nums">${{ quote.prepaymentUsd }}</span>
      </div>

    </div>

    <div class="space-y-2 pt-2">
      <button
        type="button"
        class="group relative block w-full h-11 sm:h-12 px-5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold tracking-tight shadow-md shadow-red-500/30 transition-all duration-200"
        :class="canSend
          ? 'hover:from-red-500 hover:to-red-700 active:scale-[0.99] hover:shadow-lg hover:shadow-red-500/40'
          : 'opacity-50 hover:from-red-500 hover:to-red-700'"
        :aria-disabled="!canSend"
        @click="onRequestClick"
      >
        <span class="inline-flex items-center justify-center gap-2">
          <Icon name="mdi:send-outline" class="size-5 transition-transform duration-200 group-hover:translate-x-0.5" />
          <span>{{ $t('quote.ctaSendRequest') }}</span>
        </span>
      </button>

      <p
        v-if="blockReason === 'no_dates'"
        class="text-center text-xs text-slate-500 dark:text-slate-400"
      >
        {{ $t('quote.hintPickDates') }}
      </p>

      <p
        v-if="blockReason === 'min_nights'"
        class="text-center text-xs text-amber-600 dark:text-amber-400"
      >
        {{ $t('quote.hintMinNights') }}
      </p>

      <div class="flex items-center gap-3 pt-1">
        <span class="h-px flex-1 bg-slate-200/70 dark:bg-slate-700/50" />
        <span class="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 font-semibold">
          {{ $t('quote.ctaDividerOr') }}
        </span>
        <span class="h-px flex-1 bg-slate-200/70 dark:bg-slate-700/50" />
      </div>

      <!-- Reframes the messenger buttons below as a no-commitment "just ask"
           path — the same buttons already fall back to a soft inquiry when no
           dates are picked, so this is copy only, not a duplicate CTA. -->
      <p class="text-center text-xs text-slate-500 dark:text-slate-400">
        {{ $t('askQuestion.label') }}
      </p>

      <!-- Mobile: compact 5-icon row, labels via aria-label only -->
      <div class="grid grid-cols-5 gap-1 sm:hidden">
        <a
          :href="whatsappUrl"
          target="_blank"
          rel="noopener"
          class="group flex aspect-square items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-slate-700 dark:text-slate-300 hover:ring-green-400 hover:bg-green-50 dark:hover:bg-green-500/10 active:scale-95 transition"
          :aria-label="$t('quote.ctaWhatsapp')"
          @click="onCta('whatsapp')"
        >
          <Icon name="mdi:whatsapp" class="text-green-600" style="font-size: 32px" />
        </a>
        <a
          v-if="telegramUrl"
          :href="telegramUrl"
          target="_blank"
          rel="noopener"
          class="group flex aspect-square items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-slate-700 dark:text-slate-300 hover:ring-sky-400 hover:bg-sky-50 dark:hover:bg-sky-500/10 active:scale-95 transition"
          :aria-label="$t('quote.ctaTelegram')"
          @click="onCta('telegram')"
        >
          <Icon name="ic:baseline-telegram" class="text-sky-500" style="font-size: 32px" />
        </a>
        <a
          v-if="messengerUrl"
          :href="messengerUrl"
          target="_blank"
          rel="noopener"
          class="group flex aspect-square items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-slate-700 dark:text-slate-300 hover:ring-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 active:scale-95 transition"
          :aria-label="$t('quote.ctaMessenger')"
          @click="onCta('messenger')"
        >
          <Icon name="mdi:facebook" class="text-blue-600" style="font-size: 32px" />
        </a>
        <a
          v-if="instagramUrl"
          :href="instagramUrl"
          target="_blank"
          rel="noopener"
          class="group flex aspect-square items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-slate-700 dark:text-slate-300 hover:ring-pink-400 hover:bg-pink-50 dark:hover:bg-pink-500/10 active:scale-95 transition"
          :aria-label="$t('quote.ctaInstagram')"
          @click="onCta('instagram')"
        >
          <Icon name="mdi:instagram" class="text-pink-500" style="font-size: 32px" />
        </a>
        <a
          :href="mailtoUrl"
          class="group flex aspect-square items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-slate-700 dark:text-slate-300 hover:ring-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40 active:scale-95 transition"
          :aria-label="$t('quote.ctaEmail')"
          @click="onCta('email')"
        >
          <Icon name="mdi-email-outline" class="text-slate-500" style="font-size: 32px" />
        </a>
      </div>

      <!-- sm+ : labelled vertical stack -->
      <div class="hidden sm:block sm:space-y-1">
        <a
          :href="whatsappUrl"
          target="_blank"
          rel="noopener"
          class="group flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 hover:ring-green-400 hover:bg-green-50 dark:hover:bg-green-500/10 hover:text-green-700 dark:hover:text-green-300 transition"
          @click="onCta('whatsapp')"
        >
          <Icon name="mdi:whatsapp" class="size-4 text-green-600 transition-transform group-hover:scale-110" />
          {{ $t('quote.ctaWhatsapp') }}
        </a>
        <a
          v-if="telegramUrl"
          :href="telegramUrl"
          target="_blank"
          rel="noopener"
          class="group flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 hover:ring-sky-400 hover:bg-sky-50 dark:hover:bg-sky-500/10 hover:text-sky-700 dark:hover:text-sky-300 transition"
          @click="onCta('telegram')"
        >
          <Icon name="ic:baseline-telegram" class="size-4 text-sky-500 transition-transform group-hover:scale-110" />
          {{ $t('quote.ctaTelegram') }}
        </a>
        <div
          v-if="messengerUrl || instagramUrl"
          class="grid grid-cols-2 gap-2"
        >
          <a
            v-if="messengerUrl"
            :href="messengerUrl"
            target="_blank"
            rel="noopener"
            class="group flex items-center justify-center gap-1.5 h-10 px-2 text-sm whitespace-nowrap rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 font-medium text-slate-700 dark:text-slate-300 hover:ring-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:text-blue-700 dark:hover:text-blue-300 transition"
            :class="!instagramUrl && 'col-span-2'"
            @click="onCta('messenger')"
          >
            <Icon name="mdi:facebook" class="size-4 shrink-0 text-blue-600 transition-transform group-hover:scale-110" />
            {{ $t('quote.ctaMessenger') }}
          </a>
          <a
            v-if="instagramUrl"
            :href="instagramUrl"
            target="_blank"
            rel="noopener"
            class="group flex items-center justify-center gap-1.5 h-10 px-2 text-sm whitespace-nowrap rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 font-medium text-slate-700 dark:text-slate-300 hover:ring-pink-400 hover:bg-pink-50 dark:hover:bg-pink-500/10 hover:text-pink-700 dark:hover:text-pink-300 transition"
            :class="!messengerUrl && 'col-span-2'"
            @click="onCta('instagram')"
          >
            <Icon name="mdi:instagram" class="size-4 shrink-0 text-pink-500 transition-transform group-hover:scale-110" />
            {{ $t('quote.ctaInstagram') }}
          </a>
        </div>
        <a
          :href="mailtoUrl"
          class="group flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/50 text-sm font-medium text-slate-700 dark:text-slate-300 hover:ring-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/40 hover:text-slate-900 dark:hover:text-slate-100 transition"
          @click="onCta('email')"
        >
          <Icon name="mdi-email-outline" class="size-4 text-slate-500 transition-transform group-hover:scale-110" />
          {{ $t('quote.ctaEmail') }}
        </a>
      </div>
    </div>

  </div>
</template>

<style>
/* When the widget content is taller than the viewport (long stays with
   breakdown expanded + 5 CTA buttons), it scrolls inside itself rather
   than overflowing the page. Scrollbar styled to match the card. */
.quote-widget {
  scrollbar-width: thin;
  scrollbar-color: rgb(148 163 184 / 0.4) transparent; /* slate-400 / 40 */
}
.quote-widget::-webkit-scrollbar {
  width: 6px;
}
.quote-widget::-webkit-scrollbar-track {
  background: transparent;
}
.quote-widget::-webkit-scrollbar-thumb {
  background: rgb(148 163 184 / 0.4);
  border-radius: 3px;
}
.quote-widget::-webkit-scrollbar-thumb:hover {
  background: rgb(245 158 11 / 0.55); /* amber-500 / 55 — brand accent */
}
html.dark .quote-widget {
  scrollbar-color: rgb(100 116 139 / 0.45) transparent; /* slate-500 / 45 */
}
html.dark .quote-widget::-webkit-scrollbar-thumb {
  background: rgb(100 116 139 / 0.45);
}
html.dark .quote-widget::-webkit-scrollbar-thumb:hover {
  background: rgb(245 158 11 / 0.6);
}

/* Booked-day marker on the v-calendar popover. Forced over v-calendar's
   disabled-day opacity dimming so the red stays visible. */
.vc-day .booked-day {
  background-color: rgb(254 202 202); /* red-200 */
  color: rgb(153 27 27); /* red-800 */
  font-weight: 600;
  text-decoration: line-through;
  border-radius: 0.25rem;
  opacity: 1 !important;
}
.vc-day.is-disabled .booked-day {
  opacity: 1 !important;
}

/* VueDatePicker — fill the widget column.
   The package's wrapper chain has two blind spots that defeat class-only
   overrides: (1) one wrapper has class="" with INLINE `style="--dp-menu-width: 208px"`
   that pins the calendar to ~208px wide, and (2) two more class-less
   wrappers (role="grid" / role="rowgroup") don't fill their parent.
   We catch them via [style*=…] and [role=…] attribute selectors,
   force every layer to width:100%, override dp__main's flex layout,
   and lay header + day rows out as a 7-column CSS grid. */
.quote-widget__calendar {
  display: block;
  width: 100%;
}
.quote-widget__calendar .dp__main,
.quote-widget__calendar .dp__theme_light,
.quote-widget__calendar .dp__theme_dark {
  --dp-border-color: transparent;
  --dp-border-color-hover: transparent;
  --dp-primary-color: #ef4444;
  --dp-primary-text-color: #ffffff;
  --dp-highlight-color: rgb(239 68 68 / 0.15);
  --dp-cell-border-radius: 999px;
  --dp-cell-padding: 0;
  --dp-row-margin: 0.2rem 0;
  --dp-menu-min-width: 0;
  --dp-font-family: "Montserrat", sans-serif;
}
.quote-widget__calendar .dp__main {
  display: block !important; /* override .dp__flex_display from package */
  width: 100% !important;
}
.quote-widget__calendar .dp__outer_menu_wrap,
.quote-widget__calendar .dp__menu,
.quote-widget__calendar .dp__menu_inner,
.quote-widget__calendar .dp__instance_calendar,
.quote-widget__calendar .dp__calendar,
.quote-widget__calendar .dp__calendar_header,
.quote-widget__calendar .dp__calendar_row {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
/* Catch unnamed wrappers: the one with inline --dp-menu-width, plus
   the role=grid / role=rowgroup divs vue-datepicker doesn't class. */
.quote-widget__calendar [style*="--dp-menu-width"] {
  width: 100% !important;
  display: block;
  --dp-menu-width: 100% !important;
}
.quote-widget__calendar [role="grid"],
.quote-widget__calendar [role="rowgroup"] {
  width: 100% !important;
  min-width: 0 !important;
}
.quote-widget__calendar .dp__menu,
.quote-widget__calendar .dp__menu_inner {
  border: none;
  box-shadow: none;
  padding: 0;
  background: transparent;
}
.quote-widget__calendar .dp__calendar_header,
.quote-widget__calendar .dp__calendar_row {
  display: grid !important;
  grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
  gap: 2px;
  margin: 0.2rem 0;
}
.quote-widget__calendar .dp__calendar_item {
  width: 100% !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
.quote-widget__calendar .dp__cell_inner {
  width: 100% !important;
  height: auto !important;
  aspect-ratio: 1 / 1;
  min-height: 1.75rem;
  font-variant-numeric: tabular-nums;
  border-radius: 999px;
  font-size: 0.875rem;
  padding: 0 !important;
}
.quote-widget__calendar .dp__calendar_header_item {
  width: 100% !important;
  height: auto !important;
  min-height: 1.5rem;
  padding: 0 !important;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(100 116 139); /* slate-500 */
}
html.dark .quote-widget__calendar .dp__calendar_header_item {
  color: rgb(148 163 184); /* slate-400 */
}
.quote-widget__calendar .dp__calendar_header_separator {
  background: rgb(15 44 62 / 0.1);
}
html.dark .quote-widget__calendar .dp__calendar_header_separator {
  background: rgb(255 255 255 / 0.1);
}
.quote-widget__calendar .dp__month_year_row {
  padding: 0 0 0.75rem;
  height: auto;
}
.quote-widget__calendar .dp__month_year_select {
  font-family: "Playfair Display", serif;
  font-style: italic;
  font-size: 1.05rem;
  font-weight: 600;
}
.quote-widget__calendar .dp__inner_nav {
  color: #163c52;
}
.quote-widget__calendar .dp__active_date,
.quote-widget__calendar .dp__range_end,
.quote-widget__calendar .dp__range_start {
  background: #ef4444;
  color: #ffffff;
}
.quote-widget__calendar .dp__range_between {
  background: rgb(239 68 68 / 0.18);
  color: #7f1d1d;
}
.quote-widget__calendar .dp__today {
  border-color: #ef4444;
}
/* Booked dates — clearly disabled with line-through + soft pink */
.quote-widget__calendar .dp__cell_disabled {
  background: rgb(239 68 68 / 0.04);
  color: rgb(239 68 68 / 0.35);
  text-decoration: line-through;
  text-decoration-color: rgb(239 68 68 / 0.3);
  text-decoration-thickness: 1.5px;
  cursor: not-allowed;
}
/* A selected checkout that lands on the next booking's checkin day is both
   range-end and (for a fresh checkin click) disabled — the selection look
   must win over the strike-through. */
.quote-widget__calendar .dp__range_end.dp__cell_disabled,
.quote-widget__calendar .dp__range_start.dp__cell_disabled {
  background: #ef4444;
  color: #ffffff;
  text-decoration: none;
}
/* Dark mode tuning */
html.dark .quote-widget__calendar .dp__theme_light,
html.dark .quote-widget__calendar .dp__theme_dark {
  --dp-background-color: transparent;
  --dp-text-color: #e2e8f0;
  --dp-primary-text-color: #ffffff;
  --dp-hover-color: rgb(239 68 68 / 0.12);
}
html.dark .quote-widget__calendar .dp__inner_nav {
  color: #e2e8f0;
}
html.dark .quote-widget__calendar .dp__range_between {
  color: #fecaca;
  background: rgb(239 68 68 / 0.22);
}
html.dark .quote-widget__calendar .dp__cell_disabled {
  background: rgb(239 68 68 / 0.06);
  color: rgb(252 165 165 / 0.35);
  text-decoration-color: rgb(252 165 165 / 0.3);
}
</style>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, defineAsyncComponent } from 'vue';
// Async import on purpose: Nuxt route-prefetch on visible <NuxtLink>s pulls
// the apartment page chunk AND its static deps — a sync vuepic import here
// dragged the 84 KiB datepicker chunk onto / and every page linking to
// apartments (PSI 2026-06-11: fetched at ~4.5s idle, 58 KiB unused).
// Dynamic import() is not executed by route-prefetch, so the chunk now
// loads only when this widget actually hydrates (hydrate-on-visible on
// /apartment/[id]). Safe: the picker sits inside <ClientOnly>, so there is
// no SSR markup to mismatch and the calendar already appeared post-
// hydration before this change. CSS stays static — it ships with the page
// CSS chunk and avoids a FOUC on the inline calendar.
import '@vuepic/vue-datepicker/dist/main.css';
import { useBookedDates } from '~/composables/useBookedDates';
import type { QuoteResult } from '~/composables/useQuote';
import { addDays, toIsoLocal, parseIsoLocal, formatDMY } from '~/utils/iso-date';
import { expandBookedNights, isDisabledForSelection } from '~/utils/booking-calendar';
import { getMaxBookableDate } from '~~/utils/booking-window';
import { quoteState } from '~/utils/quoteState';
import { MIN_BOOKING_REQUEST_NIGHTS } from '~~/server/services/quote-engine';
import {
  buildWhatsappUrl,
  buildMailtoUrl,
  buildTelegramUrl,
  buildSimpleWhatsappUrl,
  buildSimpleMailtoUrl,
  buildSimpleTelegramUrl,
} from '~/utils/booking-message';

const VueDatePicker = defineAsyncComponent(() =>
  import('@vuepic/vue-datepicker').then((m) => m.VueDatePicker),
);

type Props = {
  listingId: number;
  title: string;
  capacity: number;
  quote: QuoteResult | null;
  quotePending: boolean;
  quoteError: Error | null;
};
const props = defineProps<Props>();

const { locale, t } = useI18n();
const config = useRuntimeConfig();
const route = useRoute();
const localePath = useLocalePath();

// Client-only refs — avoids SSR hydration mismatch.
// VueDatePicker's range v-model is a 2-tuple [startDate, endDate] of local-
// midnight Date objects. toIsoLocal extracts YYYY-MM-DD via local getters.
const minDate = ref<Date | null>(null);
const maxDate = ref<Date | null>(null);

// dateRange/guests are owned by the parent page so /apartment/[id] can wire
// the same state into useQuote and into the hero "All apartments" CTA above
// the widget.
const dateRange = defineModel<[Date, Date] | null>('dateRange', { default: null });
const guests = defineModel<number>('guests', { default: 2 });

const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === 'dark');

// One-shot hydration from query params (e.g. /apartment/:id?from=&to=&guests=)
// so the widget arrives pre-filled when the user comes from /search.
// Dates are parsed as local-midnight (parseIsoLocal from ~/utils/iso-date)
// to match what VueDatePicker produces.
function hydrateFromQuery() {
  const fromIso = typeof route.query.from === 'string' ? route.query.from : '';
  const toIso = typeof route.query.to === 'string' ? route.query.to : '';
  const guestsQ = Number(route.query.guests);

  if (Number.isFinite(guestsQ) && guestsQ > 0) {
    guests.value = Math.min(Math.max(1, Math.trunc(guestsQ)), props.capacity);
  }

  if (!fromIso || !toIso) return;
  const start = parseIsoLocal(fromIso);
  const end = parseIsoLocal(toIso);
  if (!start || !end) return;
  if (start >= end) return;
  if (minDate.value && start < minDate.value) return;

  dateRange.value = [start, end];
}

onMounted(() => {
  const now = new Date();
  minDate.value = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  maxDate.value = getMaxBookableDate(Number(config.public.bookingWindowDays), now);
  hydrateFromQuery();
});

const checkin = computed(() => (dateRange.value?.[0] ? toIsoLocal(dateRange.value[0]) : ''));
const checkout = computed(() => (dateRange.value?.[1] ? toIsoLocal(dateRange.value[1]) : ''));

// Format for VueDatePicker input display — returns DD/MM/YYYY (no time).
const formatDate = (d: Date | Date[]) => {
  const dates = Array.isArray(d) ? d : [d];
  return dates.filter(Boolean).map((x) => formatDMY(toIsoLocal(x))).join(' → ');
};

const windowFrom = computed(() => (minDate.value ? toIsoLocal(minDate.value) : ''));
const windowTo = computed(() => (minDate.value ? addDays(minDate.value, 365) : ''));

const listingIdRef = computed(() => props.listingId);

const { booked } = useBookedDates(listingIdRef, windowFrom, windowTo);

// Role-aware disabled logic: a booking's checkin day is blocked as a
// CHECK-IN date but selectable as a CHECK-OUT date (the night before it is
// free). pendingStart tracks the in-progress range: set on the first click,
// cleared once the range completes or resets, so isDateDisabled knows which
// role the next click plays. noDisabledRange (template) stops ranges that
// would jump over a booking.
const bookedNights = computed<Set<string>>(() => expandBookedNights(booked.value));

const pendingStart = ref<string | null>(null);

// Checkin of the currently completed range — keeps the next booking's
// checkin day clickable after selection so a click on it can still mean
// "move my checkout there" (intercepted in onRangeStart below).
const completedCheckin = computed(() =>
  dateRange.value?.[0] && dateRange.value?.[1] ? checkin.value : null,
);

function onRangeStart(d: Date) {
  const iso = toIsoLocal(d);
  if (bookedNights.value.has(iso)) {
    // A checkout-only day clicked as a new range start: the user is moving
    // the checkout of the already-selected range. Rebuild the range from
    // the previous checkin (dateRange still holds it — auto-apply only
    // commits on range completion).
    const prevCheckin = completedCheckin.value;
    pendingStart.value = null;
    const start = prevCheckin && prevCheckin < iso ? parseIsoLocal(prevCheckin) : null;
    const end = parseIsoLocal(iso);
    nextTick(() => {
      dateRange.value = start && end ? [start, end] : dateRange.value ? [...dateRange.value] : null;
    });
    return;
  }
  pendingStart.value = iso;
}

watch(dateRange, (v) => {
  if (!v || (v[0] && v[1])) pendingStart.value = null;
});

function isDateDisabled(d: Date): boolean {
  return isDisabledForSelection(
    toIsoLocal(d),
    booked.value,
    bookedNights.value,
    pendingStart.value,
    completedCheckin.value,
  );
}

function isBookedDate(d: Date): boolean {
  return bookedNights.value.has(toIsoLocal(d));
}

// Highlight config — red cells for booked days so guests see them at a
// glance. Built from local-midnight Dates to match what VueDatePicker
// renders (UTC-midnight Dates shift a day in negative-offset timezones).
const highlightedDates = computed(() => ({
  dates: [...bookedNights.value].map(parseIsoLocal).filter((d): d is Date => d !== null),
}));

// quote/pending/error are owned by the parent page (see useQuote there) so
// the search-CTA banner can read the same `available` flag via provide/inject.
const unavailableMessage = computed(() => {
  if (!props.quote || props.quote.available) return '';
  const r = props.quote.reason;
  if (!r) return t('quote.errorGeneric');
  return t(`quote.unavailable.${r}`, { capacity: String(props.capacity) });
});

const canSend = computed(
  () => props.quote?.available === true && (props.quote?.nights ?? 0) >= MIN_BOOKING_REQUEST_NIGHTS,
);

// Why the send-request CTA is (or isn't) actionable — drives both the analytics
// `quote_state` and the blocked-CTA feedback below. Single source of truth via
// the shared util so onCta / onRequestClick can't drift apart.
const blockReason = computed(() =>
  quoteState({
    available: props.quote?.available,
    hasCheckin: !!checkin.value,
    hasCheckout: !!checkout.value,
    nights: props.quote?.nights,
  }),
);

// Group consecutive nights with the same rate into one breakdown row.
// Mirrors Airbnb: one "$rate × N nights" row per pricing period in the
// stay. nightlyBreakdown is ordered by date, so a single linear pass
// is enough — we don't need to sort or dedupe.
type BreakdownGroup = { rate: number; nights: number; subtotal: number; seasonName: string };
const breakdownGroups = computed<BreakdownGroup[]>(() => {
  const nb = props.quote?.nightlyBreakdown;
  if (!Array.isArray(nb) || nb.length === 0) return [];
  const groups: BreakdownGroup[] = [];
  for (const n of nb) {
    const last = groups[groups.length - 1];
    if (last && last.rate === n.rate) {
      last.nights++;
      last.subtotal += n.rate;
    } else {
      groups.push({ rate: n.rate, nights: 1, subtotal: n.rate, seasonName: n.seasonName });
    }
  }
  return groups;
});

// Free mid-stay cleanings: one after each full 5-day block.
// 5 nights → 0, 6..10 → 1, 11..15 → 2, etc. Mirrors prices/QuoteRowItem.vue
// so guests see the same calculation in both the calculator and the widget.
const cleaningsIncluded = computed(() => {
  const q = props.quote;
  if (!q?.available) return 0;
  return Math.max(0, Math.floor((q.nights - 1) / 5));
});

// Average extra-guest rate per night, for the breakdown row label.
const extraGuestRate = computed(() => {
  const q = props.quote;
  if (!q?.available || !q.nights || !q.extraGuestUsd) return 0;
  return Math.round(q.extraGuestUsd / q.nights);
});

const messageInput = computed(() => ({
  apartmentTitle: props.title,
  listingId: props.listingId,
  checkin: checkin.value,
  checkout: checkout.value,
  guests: guests.value,
  totalUsd: props.quote?.totalUsd ?? 0,
  nights: props.quote?.nights ?? 0,
  tier: (props.quote?.tier ?? 'daily') as 'daily' | 'weekly',
  poolIncluded: props.quote?.poolIncluded ?? false,
  locale: locale.value,
}));

// Generic-inquiry payload for the no-/partial-quote path. Dates are passed
// only when both endpoints are picked (even if unavailable) so the message
// can name them.
const simpleInput = computed(() => ({
  apartmentTitle: props.title,
  listingId: props.listingId,
  locale: locale.value,
  ...(checkin.value && checkout.value
    ? { checkin: checkin.value, checkout: checkout.value, guests: guests.value }
    : {}),
}));

const whatsappUrl = computed(() =>
  canSend.value
    ? buildWhatsappUrl(config.public.whatsappNumber as string, messageInput.value)
    : buildSimpleWhatsappUrl(config.public.whatsappNumber as string, simpleInput.value),
);

const mailtoUrl = computed(() =>
  canSend.value
    ? buildMailtoUrl(config.public.contactEmail as string, messageInput.value)
    : buildSimpleMailtoUrl(config.public.contactEmail as string, simpleInput.value),
);

const telegramUrl = computed(() => {
  const link = (config.public.telegramLink as string) ?? '';
  if (!link) return '';
  return canSend.value
    ? buildTelegramUrl(link, messageInput.value)
    : buildSimpleTelegramUrl(link, simpleInput.value);
});

// Messenger and Instagram carry no pre-filled text (their APIs forbid it), so
// they just open the chat — gated only by the env link being present.
const messengerUrl = computed(() => (config.public.messengerLink as string) ?? '');
const instagramUrl = computed(() => (config.public.instagramDmLink as string) ?? '');

const { trackEvent } = useAnalytics();

function onCta(channel: 'whatsapp' | 'telegram' | 'messenger' | 'instagram' | 'email') {
  trackEvent('booking_cta_click', {
    channel,
    quote_state: blockReason.value,
    listing_id: props.listingId,
    nights: props.quote?.nights ?? 0,
    tier: props.quote?.tier ?? null,
    total_usd: props.quote?.totalUsd ?? 0,
    pool_included: props.quote?.poolIncluded ?? false,
  });
}

function onRequestClick() {
  if (canSend.value) {
    trackEvent('booking_request_open', {
      listing_id: props.listingId,
      nights: props.quote?.nights ?? 0,
      total_usd: props.quote?.totalUsd ?? 0,
    });
    const target = document.getElementById('booking-request-panel');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the name input after the smooth-scroll settles. Panel listens
      // for this CustomEvent — avoids ref drilling between sibling components.
      setTimeout(() => window.dispatchEvent(new CustomEvent('booking-request-focus')), 500);
    }
    return;
  }
  // Blocked (no dates / unavailable): never a silent no-op. Record the intent
  // (so we can size this leak) and bounce the user back to the calendar — the
  // no_dates hint under the button tells them what to do next.
  trackEvent('booking_request_blocked', {
    listing_id: props.listingId,
    reason: blockReason.value,
    nights: props.quote?.nights ?? 0,
    total_usd: props.quote?.totalUsd ?? 0,
  });
  document
    .getElementById('booking-quote-widget')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function onFallbackClick() {
  trackEvent('search_all_cta_click', {
    state: 'C',
    listing_id: props.listingId,
    reason: props.quote?.reason ?? null,
  });
}
</script>
